import { createContext, useContext, useEffect, useMemo, useState } from "react";

// ZEASTRO supports both themes. Dark is the brand default (cosmic starfield),
// light is a fully supported alternative. "system" follows the OS setting.
export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  theme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  setTheme: (theme: ResolvedTheme) => void;
}

const STORAGE_KEY = "cosmic-theme";

function systemTheme(): ResolvedTheme {
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

function loadMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // ignore storage failures (private mode, etc.)
  }
  return "dark"; // brand default
}

function applyTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.classList.toggle("light", theme === "light");
  root.style.colorScheme = theme;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: "dark",
  theme: "dark",
  setMode: () => {},
  toggleTheme: () => {},
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(loadMode);
  const [sysTheme, setSysTheme] = useState<ResolvedTheme>(systemTheme);
  const theme: ResolvedTheme = mode === "system" ? sysTheme : mode;

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS changes while in "system" mode (state-driven so consumers like
  // the toggle icon stay in sync with the applied class).
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setSysTheme(systemTheme());
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  const value = useMemo<ThemeContextType>(() => {
    const setMode = (m: ThemeMode) => {
      setModeState(m);
      try {
        localStorage.setItem(STORAGE_KEY, m);
      } catch {
        // ignore
      }
    };
    return {
      mode,
      theme,
      setMode,
      setTheme: (t: ResolvedTheme) => setMode(t),
      toggleTheme: () => setMode(theme === "dark" ? "light" : "dark"),
    };
  }, [mode, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export const THEME_MODES: ThemeMode[] = ["light", "dark", "system"];
